import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { CheckCircle, InfoCircle, WarningTriangle, Xmark, XmarkCircle, } from 'iconoir-react';
import { CircleQuestionMark, Download, LoaderCircle } from 'lucide-react';
import { Children, isValidElement, useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { classNames } from './utils';
import Button from './Button';
import { Presence } from './motion/Presence';
const SNACKBAR_HEIGHT = 52;
const SNACKBAR_GAP = 8;
const SNACKBAR_STACK_OFFSET = 4;
const SNACKBAR_STACK_INSET = 20 / 420;
const SNACKBAR_STACK_COLLAPSE_DELAY = 250;
const MAX_VISIBLE_STACK_LAYERS = 3;
/**
 * Where layer `index` (0 = front) of a collapsible stack sits: fanned out
 * when expanded, else tucked behind the front one, up to three layers deep.
 */
export const getStackLayer = (index, itemCount, itemHeight, inset) => {
    const collapsedIndex = Math.min(index, MAX_VISIBLE_STACK_LAYERS - 1);
    return {
        isCollapsedLayer: index < MAX_VISIBLE_STACK_LAYERS,
        collapsedScale: 1 - collapsedIndex * inset,
        collapsedY: collapsedIndex * SNACKBAR_STACK_OFFSET,
        expandedY: index * (itemHeight + SNACKBAR_GAP),
        zIndex: itemCount - index,
    };
};
export const getStackHeights = (itemCount, itemHeight) => ({
    collapsed: itemHeight +
        (Math.min(itemCount, MAX_VISIBLE_STACK_LAYERS) - 1) * SNACKBAR_STACK_OFFSET,
    expanded: itemCount * itemHeight + (itemCount - 1) * SNACKBAR_GAP,
});
/** Expands at once and collapses a beat after the pointer leaves. */
export function useStackExpansion() {
    const [isExpanded, setIsExpanded] = useState(false);
    const collapseTimeoutRef = useRef(undefined);
    useEffect(() => () => clearTimeout(collapseTimeoutRef.current), []);
    const expand = useCallback(() => {
        clearTimeout(collapseTimeoutRef.current);
        setIsExpanded(true);
    }, []);
    const collapse = useCallback(() => {
        clearTimeout(collapseTimeoutRef.current);
        setIsExpanded(false);
    }, []);
    const scheduleCollapse = useCallback(() => {
        clearTimeout(collapseTimeoutRef.current);
        collapseTimeoutRef.current = setTimeout(() => setIsExpanded(false), SNACKBAR_STACK_COLLAPSE_DELAY);
    }, []);
    return { isExpanded, expand, collapse, scheduleCollapse };
}
const statusIconVariants = cva('flex size-7 shrink-0 items-center justify-center rounded-lg', {
    variants: {
        type: {
            error: 'bg-error-500/10 text-error-500 dark:bg-error-50 dark:text-error-700',
            info: 'bg-base-100 text-base-500 dark:bg-base-300 dark:text-base-700',
            loading: 'bg-brand-50 text-brand-500 dark:text-brand-400',
            question: 'bg-brand-500/10 text-brand-500 dark:bg-brand-50 dark:text-brand-400',
            success: 'bg-success-500/10 text-success-500 dark:bg-success-50 dark:text-success-700',
            warn: 'bg-warning-500/10 text-warning-500 dark:bg-warning-50 dark:text-warning-700',
        },
    },
    defaultVariants: {
        type: 'info',
    },
});
function SnackbarStatusIcon({ type }) {
    const iconProps = {
        'aria-hidden': true,
        'className': 'size-4',
        'strokeWidth': 1.5,
    };
    return (_jsxs("div", { className: statusIconVariants({ type }), children: [type === 'success' && _jsx(CheckCircle, Object.assign({}, iconProps)), type === 'error' && _jsx(XmarkCircle, Object.assign({}, iconProps)), type === 'warn' && _jsx(WarningTriangle, Object.assign({}, iconProps)), type === 'info' && _jsx(InfoCircle, Object.assign({}, iconProps)), type === 'question' && _jsx(CircleQuestionMark, Object.assign({}, iconProps)), type === 'loading' && (_jsx(LoaderCircle, Object.assign({}, iconProps, { className: "size-4 animate-spin" })))] }));
}
function SnackbarActions({ buttons }) {
    const actions = [...buttons].reverse();
    return (_jsx("div", { "data-snackbar-actions": true, className: "flex max-w-full shrink-0 flex-wrap items-center justify-start gap-1 lg:justify-end lg:gap-1.5", children: actions.map((button, index) => {
            var _a;
            const isPrimary = button.icon === 'download' ||
                (buttons.length > 1 && index === actions.length - 1);
            return (_jsxs(Button, { "data-snackbar-action": true, size: "xs", color: button.color, variant: (_a = button.variant) !== null && _a !== void 0 ? _a : (isPrimary ? 'default' : 'outline'), className: classNames('h-7 gap-1 rounded-lg px-2 text-xs leading-none has-[>svg]:px-2 lg:gap-1.5', isPrimary &&
                    'bg-[var(--snackbar-action-background)] text-[var(--snackbar-action-foreground)] hover:bg-[var(--snackbar-action-hover)] focus-visible:ring-[var(--snackbar-action-focus-ring)] active:bg-[var(--snackbar-action-background)]', !isPrimary &&
                    'dark:border-base-500/70 focus-visible:ring-base-500'), onClick: button.onClick, children: [button.icon === 'download' && (_jsx(Download, { className: "size-3.5", "aria-hidden": "true", strokeWidth: 1.5 })), button.title] }, button.title));
        }) }));
}
export function SnackbarStack({ children }) {
    const { isExpanded, expand, collapse, scheduleCollapse } = useStackExpansion();
    const [isCollapsible, setIsCollapsible] = useState(false);
    const previousItemCountRef = useRef(0);
    const previousNewestItemKeyRef = useRef(null);
    const wasFlowingRef = useRef(false);
    const stackRef = useRef(null);
    const chronologicalItems = Children.toArray(children);
    const items = isCollapsible
        ? [...chronologicalItems].reverse()
        : chronologicalItems;
    const itemCount = items.length;
    const newestItem = chronologicalItems[chronologicalItems.length - 1];
    const newestItemKey = isValidElement(newestItem)
        ? newestItem.key
        : String(newestItem);
    useLayoutEffect(() => {
        const collapsibleMediaQuery = window.matchMedia('(min-width: 64rem)');
        const updateIsCollapsible = () => {
            const nextIsCollapsible = collapsibleMediaQuery.matches;
            setIsCollapsible(nextIsCollapsible);
            if (!nextIsCollapsible) {
                collapse();
            }
        };
        updateIsCollapsible();
        collapsibleMediaQuery.addEventListener('change', updateIsCollapsible);
        return () => {
            collapsibleMediaQuery.removeEventListener('change', updateIsCollapsible);
        };
    }, [collapse]);
    useLayoutEffect(() => {
        const stack = stackRef.current;
        const isFlowing = !isCollapsible;
        const enteredFlowing = isFlowing && !wasFlowingRef.current;
        const itemAdded = itemCount > previousItemCountRef.current;
        const newestItemReordered = itemCount === previousItemCountRef.current &&
            newestItemKey !== previousNewestItemKeyRef.current;
        const focusInsideStack = stack === null || stack === void 0 ? void 0 : stack.contains(document.activeElement);
        if (stack &&
            isFlowing &&
            !focusInsideStack &&
            (enteredFlowing || itemAdded || newestItemReordered)) {
            stack.scrollTop = stack.scrollHeight;
        }
        previousItemCountRef.current = itemCount;
        previousNewestItemKeyRef.current = newestItemKey;
        wasFlowingRef.current = isFlowing;
    }, [isCollapsible, itemCount, newestItemKey]);
    if (!itemCount)
        return null;
    const heights = getStackHeights(itemCount, SNACKBAR_HEIGHT);
    const stackStyle = {
        '--snackbar-stack-collapsed-height': `${heights.collapsed}px`,
        '--snackbar-stack-expanded-height': `${heights.expanded}px`,
    };
    return (_jsx("ol", { ref: stackRef, className: "snackbar-stack", "data-expanded": isExpanded, style: stackStyle, onPointerEnter: isCollapsible ? expand : undefined, onPointerLeave: isCollapsible ? scheduleCollapse : undefined, children: items.map((item, index) => {
            const distanceFromNewest = isCollapsible
                ? index
                : itemCount - 1 - index;
            const layer = getStackLayer(distanceFromNewest, itemCount, SNACKBAR_HEIGHT, SNACKBAR_STACK_INSET);
            const itemStyle = {
                '--snackbar-stack-collapsed-opacity': layer.isCollapsedLayer ? 1 : 0,
                '--snackbar-stack-collapsed-scale': layer.collapsedScale,
                '--snackbar-stack-collapsed-y': `${layer.collapsedY}px`,
                '--snackbar-stack-expanded-y': `${layer.expandedY}px`,
                '--snackbar-stack-z-index': layer.zIndex,
            };
            const itemKey = isValidElement(item) ? item.key : String(item);
            return (_jsx("li", { className: "snackbar-stack__item", style: itemStyle, children: item }, itemKey));
        }) }));
}
export default function Snackbar({ type = 'info', title, description, show, buttons, afterLeave, onClose, onPointerEnter, onPointerLeave, }) {
    return (_jsx(Presence, { present: show, onExitComplete: afterLeave, children: _jsx("div", { "data-state": show ? 'open' : 'closed', className: "border-border text-popover-foreground group fade-in-0 fade-out-0 slide-in-from-bottom-2 slide-out-to-bottom-2 data-[state=closed]:animate-out data-[state=closed]:duration-fast data-[state=closed]:ease-exit data-[state=open]:animate-in data-[state=open]:duration-slow data-[state=open]:ease-enter lg:slide-in-from-right-2 lg:slide-in-from-bottom-0 lg:slide-out-to-right-2 lg:slide-out-to-bottom-0 pointer-events-auto w-full overflow-visible rounded-xl border bg-[var(--snackbar-background)] [box-shadow:var(--snackbar-shadow)] lg:rounded-[calc(var(--radius-lg)_+_0.625rem)] dark:border-transparent", children: _jsxs("div", { "data-snackbar-card": true, className: "relative flex min-h-[50px] items-start gap-2 p-2.5", onClick: (event) => event.stopPropagation(), onPointerEnter: onPointerEnter, onPointerLeave: onPointerLeave, children: [_jsx(SnackbarStatusIcon, { type: type }), _jsxs("div", { "data-snackbar-content": true, className: "flex min-w-0 flex-1 flex-wrap items-start gap-x-2 gap-y-1.5 lg:flex-nowrap lg:gap-2", children: [_jsxs("div", { className: "flex min-h-7 min-w-0 flex-[1_1_8rem] flex-col justify-center gap-0.5 pr-1 lg:min-h-0 lg:flex-1", children: [title && (_jsx("p", { "data-snackbar-copy": true, className: "text-base-900 line-clamp-5 text-[13px] leading-4 font-medium break-words lg:break-normal", children: title })), description && (_jsx("p", { "data-snackbar-copy": true, className: "text-text-tertiary line-clamp-3 text-xs leading-4 font-normal break-words lg:truncate lg:break-normal lg:whitespace-nowrap", children: description }))] }), !!(buttons === null || buttons === void 0 ? void 0 : buttons.length) && _jsx(SnackbarActions, { buttons: buttons })] }), _jsx(Button, { type: "button", "data-snackbar-close": true, size: "icon-sm", variant: "outline", "aria-label": "Close notification", className: "focus-visible:ring-base-500 relative size-7 shrink-0 self-start rounded-full max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none max-lg:text-shadow-none max-lg:after:absolute max-lg:after:-inset-2 max-lg:after:content-[''] max-lg:active:ring-0 lg:pointer-events-none lg:absolute lg:-top-2 lg:-left-2 lg:size-6 lg:opacity-0 lg:group-hover:pointer-events-auto lg:group-hover:opacity-100 lg:focus-visible:pointer-events-auto lg:focus-visible:opacity-100", onClick: onClose, children: _jsx(Xmark, { className: "size-3.5", "aria-hidden": "true", strokeWidth: 1.5 }) })] }) }) }));
}
